import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { deleteRole } from "api/graphQL_role";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { MdDelete } from "react-icons/md";
import { Select as AntSelect } from "antd";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

const DeleteRoleModal = ({
  name, id, fetchRole, data, roles,allRoleData
}: {
  name: string;
  id: string;
  fetchRole: () => void;
  data: any;
  roles: any;
  allRoleData:any;
}) => {
  const filteredRoles = roles.filter((role: any) => role.role_name !== name);
  const filteredAllRoles = allRoleData.filter((role: any) => role.role_name !== name);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCloseModal, setIsCloseModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [roleError, setRoleError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const hasUsers = data?.users && data.users.length > 0;
  const hasOtherRoles = filteredRoles.length > 0;

  const handleClose = () => {
    setSelectedRole("");
    setRoleError("");
    setConfirmed(false);
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const handleDelete = () => {
    if (hasUsers && selectedRole === "") {
      setRoleError("Please select a role to reassign users");
      return;
    }
    setIsLoading(true);
    deleteRole(id, selectedRole || "")
      .then(() => { fetchRole(); toast.success("Role deleted successfully"); handleClose(); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Role deletion failed"))
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
        <PopoverTrigger>
          <button onClick={onOpen} className="rl-del-trigger" aria-label="Delete role" onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
            <MdDelete size={16}/>
          </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #fecaca" borderRadius="12px" bg="#fef2f2" maxW="180px" p="0" overflow="hidden" zIndex="20">
          <PopoverHeader bg="#fee2e2" fontSize="12px" fontWeight="700" color="#b91c1c" borderBottom="1px solid #fecaca" px="12px" py="9px">
            Delete Role
          </PopoverHeader>
          <PopoverArrow bg="#fef2f2"/>
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)"/>

        <div className={`rdm-panel${isOpen && !isCloseModal ? " rdm-panel--open" : " rdm-panel--close"}`}>
          {/* Header */}
          <div className="rdm-header">
            <div className="rdm-header-left">
              <div className="rdm-header-icon">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4h6v2"/>
                </svg>
              </div>
              <div>
                <h2 className="rdm-header-title">Delete Role</h2>
                <p className="rdm-header-sub">This action cannot be undone</p>
              </div>
            </div>
            <button className="rdm-close-btn" onClick={handleClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="rdm-body">
            {/* Role preview */}
            <div className="rdm-role-preview">
              <div className="rdm-role-avatar">{name?.charAt(0)?.toUpperCase() || "R"}</div>
              <div>
                <p className="rdm-role-name">{name}</p>
                <p className="rdm-role-id">ID: {id}</p>
              </div>
            </div>

            {data?.users && data?.users.length > 0 ? (
              <>
                {/* Warning */}
                <div className="rdm-warning-card">
                  <div className="rdm-warning-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div className="rdm-warning-content">
                    <p className="rdm-warning-title">Users need to be reassigned</p>
                    <p className="rdm-warning-desc">
                      <strong>{name}</strong> is assigned to {data.users.length} user{data.users.length !== 1 ? "s" : ""}. Select another role to reassign them before deleting.
                    </p>
                  </div>
                </div>

                {/* User list */}
                <div className="rdm-user-list-card">
                  <p className="rdm-user-list-title">Assigned Users ({data.users.length})</p>
                  <div className="rdm-user-list">
                    {data.users.map((user: any) => (
                      <div key={user.id} className="rdm-user-item">
                        <div className="rdm-user-avatar">{user.name?.charAt(0)?.toUpperCase() || "U"}</div>
                        <span className="rdm-user-name">{user.name}</span>
                        <span className="rdm-user-id">{user.id}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reassign select */}
                <div className="rdm-reassign-wrap">
                  <label className="rdm-reassign-label">
                    Reassign users to <span className="rdm-req">*</span>
                  </label>
                  <AntSelect
                    showSearch
                    placeholder="Select replacement role…"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.label ?? "").toUpperCase().includes(input.toUpperCase())
                    }
                    filterSort={(a, b) =>
                      String(a?.label ?? "").toLowerCase().localeCompare(String(b?.label ?? "").toLowerCase())
                    }
                    className="rdm-ant-select"
                    style={{ width: "100%" }}
                    options={filteredAllRoles.map((role: any) => ({
                      value: role?.id,
                      label: String(role?.role_name),
                    }))}
                    value={selectedRole || undefined}
                    onChange={(e) => { setSelectedRole(e); setRoleError(""); }}
                    notFoundContent="No roles found"
                    status={roleError ? "error" : ""}
                  />
                  {roleError && <p className="rdm-field-err">{roleError}</p>}
                </div>
              </>
            ) : (
              <>
                <div className="rdm-warning-card">
                  <div className="rdm-warning-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div className="rdm-warning-content">
                    <p className="rdm-warning-title">Irreversible Action</p>
                    <p className="rdm-warning-desc">
                      Deleting <strong>{name}</strong> will permanently remove this role and all associated permission configurations.
                    </p>
                  </div>
                </div>

                <div className="rdm-consequences">
                  <p className="rdm-consequences-title">What will be deleted:</p>
                  <div className="rdm-consequence-list">
                    {["Role name and configuration","All module permission settings","Role assignment history"].map((item, i) => (
                      <div key={i} className="rdm-consequence-item">
                        <div className="rdm-consequence-dot"/>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <label className="rdm-confirm-row">
                  <input type="checkbox" className="rdm-confirm-check" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}/>
                  <span className="rdm-confirm-label">I understand this action is permanent and cannot be undone</span>
                </label>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="rdm-footer">
            <div className="rdm-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Confirm your intent before proceeding
            </div>
            <div className="rdm-footer-btns">
              <button className="rdm-btn-cancel" onClick={handleClose}>Cancel</button>
              {hasUsers ? (
                hasOtherRoles && (
                  <button className="rdm-btn-delete" onClick={handleDelete} disabled={isLoading}>
                    {isLoading ? <span className="rdm-spinner"/> : (
                      <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>Reassign &amp; Delete</>
                    )}
                  </button>
                )
              ) : (
                <button
                  className={`rdm-btn-delete${confirmed ? "" : " rdm-btn-delete--disabled"}`}
                  onClick={handleDelete}
                  disabled={!confirmed || isLoading}
                >
                  {isLoading ? <span className="rdm-spinner"/> : (
                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>Delete Role</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .rl-del-trigger {
          width: 32px; height: 32px; border-radius: 8px;
          background: #fff7f7; border: 1.5px solid #fecaca; color: #dc2626;
          cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .15s;
        }
        .rl-del-trigger:hover { background: #fee2e2; border-color: #fca5a5; }

        .rdm-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 580px;
          background: #fff; z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -8px 0 40px rgba(15,23,42,0.15);
          transition: transform .32s cubic-bezier(0.16,1,0.3,1);
        }
        .rdm-panel--open  { transform: translateX(0); }
        .rdm-panel--close { transform: translateX(100%); }

        .rdm-header {
          padding: 16.5px 28px;
          background: linear-gradient(135deg,#7f1d1d,#dc2626);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .rdm-header-left { display: flex; align-items: center; gap: 14px; }
        .rdm-header-icon {
          width: 42px; height: 42px; border-radius: 11px;
          background: rgba(255,255,255,.18); border: 1.5px solid rgba(255,255,255,.28);
          display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0;
        }
        .rdm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 22px; }
        .rdm-header-sub { font-size: 12.5px; color: rgba(255,255,255,.65); font-weight: 500; }
        .rdm-close-btn {
          background: rgba(255,255,255,.15); border: none; border-radius: 8px;
          width: 32px; height: 32px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,.8); transition: background .15s;
        }
        .rdm-close-btn:hover { background: rgba(255,255,255,.25); }

        .rdm-body { flex: 1; padding: 28px; display: flex; flex-direction: column; gap: 18px; overflow-y: auto; }
        .rdm-body::-webkit-scrollbar { width: 4px; }
        .rdm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* Role preview */
        .rdm-role-preview {
          display: flex; align-items: center; gap: 12px;
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px;
        }
        .rdm-role-avatar {
          width: 40px; height: 40px; border-radius: 10px;
          background: linear-gradient(135deg,#fee2e2,#fecaca);
          border: 1.5px solid #fca5a5;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; color: #dc2626; flex-shrink: 0;
        }
        .rdm-role-name { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
        .rdm-role-id { font-size: 11.5px; color: #94a3b8; font-weight: 500; }

        /* Warning */
        .rdm-warning-card {
          display: flex; gap: 14px;
          background: linear-gradient(135deg,#fff7f7,#fee2e2);
          border: 1.5px solid #fecaca; border-radius: 14px; padding: 18px;
        }
        .rdm-warning-card--block { flex-direction: column; }
        .rdm-warning-icon {
          width: 44px; height: 44px; border-radius: 11px;
          background: #fff; border: 1.5px solid #fecaca;
          display: flex; align-items: center; justify-content: center;
          color: #dc2626; flex-shrink: 0;
        }
        .rdm-warning-content { flex: 1; }
        .rdm-warning-title { font-size: 14px; font-weight: 700; color: #991b1b; margin-bottom: 6px; line-height: 1.3; }
        .rdm-warning-desc { font-size: 13px; color: #b91c1c; line-height: 1.55; font-weight: 500; }

        /* User list */
        .rdm-user-list-card { background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
        .rdm-user-list-title { font-size: 12.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 12px; }
        .rdm-user-list { display: flex; flex-direction: column; gap: 6px; max-height: 180px; overflow-y: auto; }
        .rdm-user-list::-webkit-scrollbar { width: 3px; }
        .rdm-user-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        .rdm-user-item { display: flex; align-items: center; gap: 10px; padding: 8px; background: #fff; border-radius: 8px; border: 1px solid #f1f5f9; }
        .rdm-user-avatar {
          width: 28px; height: 28px; border-radius: 7px;
          background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 700; color: #2563eb; flex-shrink: 0;
        }
        .rdm-user-name { font-size: 13px; font-weight: 600; color: #0f172a; flex: 1; }
        .rdm-user-id { font-size: 11px; color: #94a3b8; font-weight: 500; }

        /* Reassign */
        .rdm-reassign-wrap { display: flex; flex-direction: column; gap: 6px; }
        .rdm-reassign-label { font-size: 12.5px; font-weight: 700; color: #374151; }
        .rdm-req { color: #dc2626; }
        .rdm-ant-select {display:table !important;}
        .rdm-ant-select .ant-select-selector { height: 46px !important; border: 1.5px solid #e2e8f0 !important; border-radius: 11px !important; background: #f8fafc !important; font-family: 'Plus Jakarta Sans', sans-serif !important; }
        .rdm-ant-select .ant-select-selection-search-input { height: 44px !important; }
        .rdm-ant-select .ant-select-selection-placeholder { line-height: 44px !important; color: #cbd5e1 !important; }
        .rdm-ant-select .ant-select-selection-item { line-height: 44px !important; }
        .rdm-field-err { font-size: 12px; color: #dc2626; font-weight: 500; }

        /* Consequences */
        .rdm-consequences { background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
        .rdm-consequences-title { font-size: 12.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 12px; }
        .rdm-consequence-list { display: flex; flex-direction: column; gap: 8px; }
        .rdm-consequence-item { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: #475569; font-weight: 500; }
        .rdm-consequence-dot { width: 6px; height: 6px; border-radius: 50%; background: #ef4444; flex-shrink: 0; }

        /* Confirm */
        .rdm-confirm-row {
          display: flex; align-items: flex-start; gap: 12px; cursor: pointer;
          background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 12px; padding: 14px;
        }
        .rdm-confirm-check { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; accent-color: #dc2626; cursor: pointer; }
        .rdm-confirm-label { font-size: 13px; font-weight: 600; color: #92400e; line-height: 1.5; }

        /* Footer */
        .rdm-footer {
          padding: 16px 28px; border-top: 1px solid #f1f5f9; background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; flex-shrink: 0;
        }
        .rdm-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .rdm-footer-btns { display: flex; gap: 10px; }
        .rdm-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600; color: #64748b;
          padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .rdm-btn-cancel:hover { background: #f1f5f9; color: #374151; }
        .rdm-btn-delete {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#991b1b,#dc2626);
          cursor: pointer; box-shadow: 0 4px 14px rgba(220,38,38,.3);
          transition: transform .15s, box-shadow .18s, opacity .18s;
        }
        .rdm-btn-delete:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(220,38,38,.38); }
        .rdm-btn-delete--disabled { opacity: .45; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
        .rdm-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: rdmSpin .65s linear infinite; }
        @keyframes rdmSpin { to{transform:rotate(360deg)} }

        @media (max-width: 520px) {
          .rdm-panel { max-width: 100%; }
          .rdm-header, .rdm-body, .rdm-footer { padding-left: 20px; padding-right: 20px; }
        }
      `}</style>
    </>
  );
};

export default DeleteRoleModal;